// src/components/MarketInsights/hooks/useMarketInsights.ts

import { useQuery, UseQueryOptions } from "@tanstack/react-query";
import { useState, useCallback, useMemo } from "react";
import { fetchWithAuth, buildQueryString } from "../../../lib/api";
import {
  SummaryStats,
  FilterOptions,
  TimelineDataPoint,
  PricingStat,
  PropertiesResponse,
  UnitDetail,
  SpecialItem,
  Filters,
  GroupByOption,
  PricingHistoryGroupBy,
  PricingHistoryResponse,
  SortByOption,
  SortOrder,
} from "../types";

// =============================================================================
// API Functions - Now using authenticated fetch
// =============================================================================

async function fetchJson<T>(endpoint: string): Promise<T> {
  return fetchWithAuth<T>(endpoint);
}

// =============================================================================
// Query Keys (for cache management)
// =============================================================================

export const marketInsightsKeys = {
  all: ["market-insights"] as const,
  summary: (filters?: Partial<Filters>) =>
    [...marketInsightsKeys.all, "summary", filters] as const,
  filters: () => [...marketInsightsKeys.all, "filters"] as const,
  timeline: (filters?: Partial<Filters>) =>
    [...marketInsightsKeys.all, "timeline", filters] as const,
  unitsByDate: (date: string, filters?: Partial<Filters>) =>
    [...marketInsightsKeys.all, "units-by-date", date, filters] as const,
  pricingStats: (groupBy: GroupByOption, filters?: Partial<Filters>) =>
    [...marketInsightsKeys.all, "pricing-stats", groupBy, filters] as const,
  pricingHistory: (
    groupBy: PricingHistoryGroupBy,
    daysBack: number,
    propertyIds?: string[],
    filters?: Partial<Filters>
  ) =>
    [
      ...marketInsightsKeys.all,
      "pricing-history",
      groupBy,
      daysBack,
      propertyIds,
      filters,
    ] as const,
  properties: (
    filters?: Partial<Filters>,
    sort?: { by: SortByOption; order: SortOrder },
    pagination?: { limit: number; offset: number }
  ) =>
    [
      ...marketInsightsKeys.all,
      "properties",
      filters,
      sort,
      pagination,
    ] as const,
  specials: (filters?: Partial<Filters>) =>
    [...marketInsightsKeys.all, "specials", filters] as const,
  unitDetail: (id: string) => [...marketInsightsKeys.all, "unit", id] as const,
};

// =============================================================================
// Hooks
// =============================================================================

/**
 * Fetch market summary statistics
 */
export function useMarketSummary(
  filters?: Partial<Filters>,
  options?: Omit<UseQueryOptions<SummaryStats>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.summary(filters),
    queryFn: () => {
      const params: Record<string, any> = {};

      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }
      if (filters?.priceRange) {
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }
      if (filters?.sqftRange) {
        params.min_sqft = filters.sqftRange[0];
        params.max_sqft = filters.sqftRange[1];
      }
      if (filters?.petsAllowed !== null && filters?.petsAllowed !== undefined) {
        params.pets_allowed = filters.petsAllowed;
      }
      if (filters?.availableNowOnly) {
        params.available_now_only = true;
      }

      return fetchJson<SummaryStats>(
        `/market-insights/summary${buildQueryString(params)}`
      );
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch filter options (neighborhoods, amenities, etc.)
 */
export function useFilterOptions(
  options?: Omit<UseQueryOptions<FilterOptions>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.filters(),
    queryFn: () => fetchJson<FilterOptions>(`/market-insights/filters`),
    staleTime: 30 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch active leasing specials
 */
export function useActiveSpecials(
  filters?: Partial<Filters>,
  options?: Omit<UseQueryOptions<SpecialItem[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.specials(filters),
    queryFn: () => {
      const params: Record<string, any> = {};
      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      return fetchJson<SpecialItem[]>(
        `/market-insights/specials${buildQueryString(params)}`
      );
    },
    staleTime: 10 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch availability timeline data
 */
export function useAvailabilityTimeline(
  filters?: Partial<Filters>,
  daysAhead: number = 90,
  includeAvailableNow: boolean = false,
  options?: Omit<UseQueryOptions<TimelineDataPoint[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: [...marketInsightsKeys.timeline(filters), includeAvailableNow],
    queryFn: () => {
      const params: Record<string, any> = {
        days_ahead: daysAhead,
        include_available_now: includeAvailableNow,
      };

      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }

      return fetchJson<TimelineDataPoint[]>(
        `/market-insights/availability-timeline${buildQueryString(params)}`
      );
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch units available on a specific date
 */
export function useUnitsByDate(
  date: string | null,
  filters?: Partial<Filters>,
  options?: Omit<UseQueryOptions<UnitDetail[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.unitsByDate(date || "", filters),
    queryFn: () => {
      if (!date) return Promise.resolve([]);

      const params: Record<string, any> = {};

      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }
      if (filters?.priceRange) {
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }

      return fetchJson<UnitDetail[]>(
        `/market-insights/units-by-date/${date}${buildQueryString(params)}`
      );
    },
    enabled: !!date,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch pricing statistics grouped by dimension
 */
export function usePricingStats(
  groupBy: GroupByOption = "neighborhood",
  filters?: Partial<Filters>,
  options?: Omit<UseQueryOptions<PricingStat[]>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.pricingStats(groupBy, filters),
    queryFn: () => {
      const params: Record<string, any> = { group_by: groupBy };

      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }
      if (filters?.priceRange) {
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }
      if (filters?.sqftRange) {
        params.min_sqft = filters.sqftRange[0];
        params.max_sqft = filters.sqftRange[1];
      }
      if (filters?.petsAllowed !== null && filters?.petsAllowed !== undefined) {
        params.pets_allowed = filters.petsAllowed;
      }
      if (filters?.availableNowOnly) {
        params.available_now_only = true;
      }

      return fetchJson<PricingStat[]>(
        `/market-insights/pricing-stats${buildQueryString(params)}`
      );
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch archived pricing history grouped for trend comparison
 */
export function usePricingHistory(
  groupBy: PricingHistoryGroupBy = "bedrooms",
  daysBack: number = 90,
  propertyIds: string[] = [],
  filters?: Partial<Filters>,
  options?: Omit<UseQueryOptions<PricingHistoryResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.pricingHistory(
      groupBy,
      daysBack,
      propertyIds,
      filters
    ),
    queryFn: () => {
      const params: Record<string, any> = {
        group_by: groupBy,
        days_back: daysBack,
      };

      if (propertyIds.length) {
        params.property_ids = propertyIds;
        params.limit_series = groupBy === "floor_plan" ? 16 : 8;
      }
      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }
      if (filters?.priceRange) {
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }
      if (filters?.sqftRange) {
        params.min_sqft = filters.sqftRange[0];
        params.max_sqft = filters.sqftRange[1];
      }
      if (filters?.petsAllowed !== null && filters?.petsAllowed !== undefined) {
        params.pets_allowed = filters.petsAllowed;
      }
      if (filters?.availableNowOnly) {
        params.available_now_only = true;
      }

      return fetchJson<PricingHistoryResponse>(
        `/market-insights/pricing-history${buildQueryString(params)}`
      );
    },
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch properties list with filtering and pagination
 */
export function usePropertiesList(
  filters?: Partial<Filters>,
  sort: { by: SortByOption; order: SortOrder } = {
    by: "available_now",
    order: "desc",
  },
  pagination: { limit: number; offset: number } = { limit: 50, offset: 0 },
  options?: Omit<UseQueryOptions<PropertiesResponse>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.properties(filters, sort, pagination),
    queryFn: () => {
      const params: Record<string, any> = {
        sort_by: sort.by,
        sort_order: sort.order,
        limit: pagination.limit,
        offset: pagination.offset,
      };

      if (filters?.search) {
        params.search = filters.search;
      }
      if (filters?.neighborhoods?.length) {
        params.neighborhoods = filters.neighborhoods;
      }
      if (filters?.bedrooms?.length) {
        params.min_beds = Math.min(...filters.bedrooms);
        params.max_beds = Math.max(...filters.bedrooms);
      }
      if (filters?.priceRange) {
        params.min_price = filters.priceRange[0];
        params.max_price = filters.priceRange[1];
      }
      if (filters?.sqftRange) {
        params.min_sqft = filters.sqftRange[0];
        params.max_sqft = filters.sqftRange[1];
      }
      if (filters?.petsAllowed !== null && filters?.petsAllowed !== undefined) {
        params.pets_allowed = filters.petsAllowed;
      }
      if (filters?.availableNowOnly) {
        params.available_now_only = true;
      }

      return fetchJson<PropertiesResponse>(
        `/market-insights/properties${buildQueryString(params)}`
      );
    },
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch single unit details
 */
export function useUnitDetail(
  unitId: string | null,
  options?: Omit<UseQueryOptions<UnitDetail>, "queryKey" | "queryFn">
) {
  return useQuery({
    queryKey: marketInsightsKeys.unitDetail(unitId || ""),
    queryFn: () => {
      if (!unitId) throw new Error("Unit ID required");
      return fetchJson<UnitDetail>(`/market-insights/units/${unitId}`);
    },
    enabled: !!unitId,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

// =============================================================================
// Filter State Hook
// =============================================================================

const defaultFilters: Filters = {
  neighborhoods: [],
  bedrooms: [],
  priceRange: null,
  sqftRange: null,
  amenities: [],
  petsAllowed: null,
  availableNowOnly: false,
  search: "",
};

export function useFiltersState(initialFilters?: Partial<Filters>) {
  const [filters, setFilters] = useState<Filters>({
    ...defaultFilters,
    ...initialFilters,
  });

  const updateFilter = useCallback(
    <K extends keyof Filters>(key: K, value: Filters[K]) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const resetFilters = useCallback(() => {
    setFilters(defaultFilters);
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.neighborhoods.length > 0 ||
      filters.bedrooms.length > 0 ||
      filters.priceRange !== null ||
      filters.sqftRange !== null ||
      filters.amenities.length > 0 ||
      filters.petsAllowed !== null ||
      filters.availableNowOnly ||
      filters.search !== ""
    );
  }, [filters]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.neighborhoods.length > 0) count++;
    if (filters.bedrooms.length > 0) count++;
    if (filters.priceRange !== null) count++;
    if (filters.sqftRange !== null) count++;
    if (filters.amenities.length > 0) count++;
    if (filters.petsAllowed !== null) count++;
    if (filters.availableNowOnly) count++;
    if (filters.search !== "") count++;
    return count;
  }, [filters]);

  return {
    filters,
    setFilters,
    updateFilter,
    resetFilters,
    hasActiveFilters,
    activeFilterCount,
  };
}
